import { useMemo } from "react";

import { useGroupMessages, useKeepConversationsOpen } from "./useGroupMessages";
import {
  groupMessagesIntoTurns,
  useInfiniteTurns,
} from "./useInfiniteMessages";

/**
 * The messages the conversation view renders.
 *
 * With `keep-conversations-open` off, this is exactly {@link useInfiniteTurns}. With it on, the
 * view shows the whole group as one stream: the newest conversation still comes from the live
 * head, and the group endpoint supplies the older closed conversations for scrollback.
 * Group history is strictly older than the head, so concatenation keeps the order.
 */
export const useConversationStream = (conversationId?: string) => {
  const useGroups = useKeepConversationsOpen();

  const head = useInfiniteTurns(conversationId);

  const groupId = head.data?.pages[0]?.conversation?.conversation_group_id;
  const group = useGroupMessages(groupId ?? undefined, {
    enabled: useGroups && Boolean(groupId),
  });

  const headMessages = head.data?.allMessages;
  const groupMessages = group.data?.allMessages;

  // Hold history back until the open conversation is fully paged, so scrollback opens no gap.
  const includeHistory = useGroups && !head.hasNextPage;

  const mergedMessages = useMemo(() => {
    if (!includeHistory) return headMessages;
    const history = (groupMessages ?? []).filter(
      (message) => message.conversationId !== conversationId,
    );
    return [...history, ...(headMessages ?? [])];
  }, [includeHistory, groupMessages, headMessages, conversationId]);

  const mergedTurns = useMemo(() => {
    if (mergedMessages === headMessages) return head.data?.allTurns;
    if (!mergedMessages) return undefined;
    return groupMessagesIntoTurns(mergedMessages);
  }, [mergedMessages, headMessages, head.data?.allTurns]);

  if (!useGroups) return head;

  return {
    ...head,
    data: head.data
      ? { ...head.data, allMessages: mergedMessages, allTurns: mergedTurns }
      : head.data,
    // Page the open conversation first, then the group's older ones.
    fetchNextPage: head.hasNextPage ? head.fetchNextPage : group.fetchNextPage,
    hasNextPage: head.hasNextPage || group.hasNextPage,
    isFetchingNextPage: head.isFetchingNextPage || group.isFetchingNextPage,
  };
};
